/**
 * Why a level is paused. Several holds can overlap and each is lifted on its own terms:
 * - `player`: the pause button; lifted by the button again or a tap on the paused layer.
 * - `background`: the page was hidden (app switch, lock screen, a call). Coming back does NOT lift it — the player
 *   returns to a paused board and resumes when ready, like the pause button.
 * - an open sheet (Settings, and Graphics over it; counted with sheetOpened/sheetClosed): lifted when the last one
 *   closes, so closing Settings resumes only a pause that Settings itself caused.
 * - `guide`: Captain Bram's card that stops the action (a boss, the first flyers, the between-waves lesson; DESIGN §7).
 *   The card is the layer, so no "Paused" layer shows, and a tap on the paused layer never lifts it: only the card
 *   ending does (`release`), or the level ending (`clear`).
 */
export type PauseCause = "player" | "background" | "guide";

export class PauseState {
  private player = false;
  private background = false;
  private sheets = 0;
  private guide = false;

  /** No sim ticks while this is true. */
  get paused(): boolean { return this.player || this.background || this.sheets > 0 || this.guide; }

  /** Only the guide's card holds the level: the music stays up and the screen stays awake (it is being read). */
  get onlyGuide(): boolean { return this.guide && !this.player && !this.background && this.sheets === 0; }

  /** A Settings sheet (or Graphics over it) is open. */
  get sheetOpen(): boolean { return this.sheets > 0; }

  /**
   * Whether the "Paused — tap to resume" layer shows: only for a pause the player has to lift. A pause held only by
   * an open sheet ends with the sheet, and the sheet is what is on screen.
   */
  get showsLayer(): boolean { return this.player || this.background; }

  /** The pause button: pauses, or lifts the player's and the background pause (a sheet's hold stays until it closes). */
  toggle(): void {
    if (this.player || this.background) this.resume();
    else this.player = true;
  }

  /** A tap on the paused layer. */
  resume(): void { this.player = false; this.background = false; }

  hold(cause: PauseCause): void {
    if (cause === "player") this.player = true;
    else if (cause === "guide") this.guide = true;
    else this.background = true;
  }

  /** Lifts one cause (the guide's card ended); the others stay. */
  release(cause: PauseCause): void {
    if (cause === "player") this.player = false;
    else if (cause === "guide") this.guide = false;
    else this.background = false;
  }

  sheetOpened(): void { this.sheets++; }
  sheetClosed(): void { this.sheets = Math.max(0, this.sheets - 1); }

  /** The level ended: nothing holds any more (the end screen is not a pause). */
  clear(): void { this.player = false; this.background = false; this.sheets = 0; this.guide = false; }
}
